import { Box, Button, Card, CardContent, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, 
  InputLabel, Link, OutlinedInput, Radio, RadioGroup, Switch, Tooltip} from "@mui/material"
import { useFormik } from "formik"
import { EditBase, useEditContext, useRefresh } from "ra-core"
import { ChangeEvent, FC, useCallback, useState } from "react"
import OdForm, { OnSubmit } from "@/components/formik/OdForm"
import TextField from "@/components/formik/TextField"
import { SaveAction } from "@/dataProvider/types"
import { SsoConfigResponseDto, SsoProtocol } from "@/gen/api/user"
import * as Yup from 'yup'
import { RecPatch } from "@/dataProvider/tools"
import { useSnackbar } from "notistack"
import { useEffect } from "react"
import useIdTokenUser from "@/authProvider/useIdTokenUser"
import { useRef } from "react"
import ReadonlyField from "@/components/input/ReadonlyField"
import { autoCopyTextFieldProps } from "@/utils/clipboard"
import { useHasPermit } from "@/authProvider/useHasPermit"
import { ORGANIZATION_USERS } from "@/constants"
import { useRevealPass } from "@/utils/useRevealPass"
import { useResourceRegister } from "@/utils/resourceRegister"
import { useDispatch } from "react-redux"
import { crudGetOneEx } from "@/actions/crudFetchEx"
import retrieveAxiosErr from "@/utils/retrieveAxiosErr"


const SsoConfig: FC = (props) => {
  useResourceRegister({ name: 'settings-saml-slo-cert' })
  const idTokenUser = useIdTokenUser()
  return (
    <EditBase {...props} mutationMode={'pessimistic'} id={idTokenUser?.organization_id} >
      <SsoConfigCater {...props} />
    </EditBase>
  )
}

const SsoConfigCater: FC = (props) => {
  const { save, record } = useEditContext<SsoConfigResponseDto & RecPatch>()
  const { enqueueSnackbar } = useSnackbar()
  const refresh = useRefresh()

  const performSave = useCallback<SaveAction<SsoConfigResponseDto>>((record, redirect, callbacks) => {
    
    save && save(record, redirect, { 
      ...callbacks, 
      onSuccess: (resp: any) =>
        Promise.resolve(callbacks && callbacks.onSuccess && callbacks.onSuccess(resp)).then(() => {
          enqueueSnackbar('Update succeed', { variant: 'success' })
          refresh()
        })
    })
  }, [save, enqueueSnackbar, refresh])

  return (
    <SsoConfigBody save={performSave} preData={record} {...props} />
  )
}

export interface SsoConfigBodyProps {
  save: SaveAction<SsoConfigResponseDto>;
  preData: (SsoConfigResponseDto & RecPatch) | undefined;
}

const validationSchema =
  Yup.object().shape({
    protocol: Yup.mixed().oneOf(['0', '1']),
    samlEntityId: Yup.string().max(255).when('protocol', {
      is: (protocol: string) => protocol === '0',
      then: Yup.string().required('Service Provider Entity Id is required'),
    }),
    samlLoginUrl: Yup.string().max(255).when('protocol', {
      is: (protocol:string) => protocol === '0',
      then: Yup.string().required('Sign In URL is required'),
    }),
    samlLogoutUrl: Yup.string().max(255),

    oidcDomain: Yup.string().max(255).when('protocol', {
      is: (protocol:string) => protocol === '1',
      then: Yup.string().required('Well Known Configuration URL is required'),
    }),
    oidcClientId: Yup.string().max(255).when('protocol', {
      is: (protocol:string) => protocol === '1',
      then: Yup.string().required('Client Id is required'),
    }),
    oidcClientSecret: Yup.string().max(255).when('protocol', {
      is: (protocol:string) => protocol === '1',
      then: Yup.string().required('Client Secret is required'),
    }),
    oidcLogoutUrl: Yup.string().max(255),
  })

export const SsoConfigBody: FC<SsoConfigBodyProps> = ({
  save,
  preData,
}) => {
  const hasWritePermit = useHasPermit([ORGANIZATION_USERS])
  const dispatch = useDispatch()
  const initialValues = {
    submit: '',
    isEnabled: preData?.isEnabled || false,
    protocol: (preData?.protocol || SsoProtocol.SAML)+'',
    samlLoginUrl: preData?.samlLoginUrl || '',
    samlLogoutUrl: preData?.samlLogoutUrl || '',
    samlEntityId: preData?.samlEntityId || '',
    samlCertificate: '',
    samlAssertionConsumerServiceUrl: preData?.samlAssertionConsumerServiceUrl || '',
    samlSpIssuer: preData?.samlSpIssuer || '',
    oidcDomain: preData?.oidcDomain || '',
    oidcClientId: preData?.oidcClientId || '',
    oidcClientSecret: preData?.oidcClientSecret || '',
    oidcLogoutUrl: preData?.oidcLogoutUrl || '',
    oidcRedirectUrl: preData?.oidcRedirectUrl || '',
  }

  const preDataRef = useRef<any>(preData)
  const certFileRef = useRef<File | null>(null)  

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setTouched,
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    if (!hasWritePermit) { return }
    const options = {
      onSuccess: () => {
        setTouched({})
        setStatus({ success: true })
        setSubmitting(false)
        dispatch(crudGetOneEx('profile', 'edit', ''))
      },
      onFailure: (err: any) => {
        setTouched({})
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    save({...values, protocol: +values.protocol, samlCertificate: (certFileRef.current as any) }, '', options)
  }, [save, hasWritePermit, dispatch])

  // const [showPassword, setShowPassword] = useState(false)
  const { showPass, endAdornment } = useRevealPass()

  const validate = useCallback((values: typeof initialValues) => {
    if (!preData?.samlCertificate && +values.protocol === SsoProtocol.SAML && !certFileRef.current) {
      return { samlCertificate: 'Should choose SAML certificate to upload' }
    }
  }, [preData])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const formikCtrlRef = useRef(formikCtrl)
  formikCtrlRef.current = formikCtrl
  const { errors, touched, isSubmitting, values, handleChange, handleBlur, setFieldValue, setValues, setFieldTouched } = formikCtrl

  const handleCertFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    certFileRef.current = e.target.files ? (e.target.files[0] || null) : null
    handleChange(e)
  }, [handleChange])

  useEffect(() => {
    if (+values.protocol === SsoProtocol.OIDC && !!values.samlCertificate) {
      certFileRef.current = null
      setFieldValue('samlCertificate', '')
    }
  }, [values.protocol, values.samlCertificate, setFieldValue])

  useEffect(() => {
    // we are here because:
    // * preData changed (most likely api responses back)
    // * touched changed (we only interested in "isEnabled" switch change happened before initial data is ready)
    if (!!(preData?.oidcRedirectUrl)         // "preData" contains actual data (api responses data back) 
      && preDataRef.current !== preData      // the data changed indeed
      && !formikCtrlRef.current.isSubmitting // if its for "touched" update, we rule out the "submit" button been clicked which will set all values to be touched
    ) {
      preDataRef.current = preData
      setValues(v => ({
        isEnabled: touched.isEnabled ? v.isEnabled : (preData.isEnabled ?? false),
        protocol: (preData.protocol || SsoProtocol.SAML)+'',
        samlLoginUrl: preData.samlLoginUrl || '',
        samlLogoutUrl: preData.samlLogoutUrl || '',
        samlEntityId: preData.samlEntityId || '',
        samlCertificate: '',
        samlAssertionConsumerServiceUrl: preData.samlAssertionConsumerServiceUrl || '',
        samlSpIssuer: preData.samlSpIssuer || '',
        oidcDomain: preData.oidcDomain || '',
        oidcClientId: preData.oidcClientId || '',
        oidcClientSecret: preData.oidcClientSecret || '',
        oidcLogoutUrl: preData.oidcLogoutUrl || '',
        oidcRedirectUrl: preData.oidcRedirectUrl || '',
      }) as any)
    }
  }, [preData, setValues, touched])

  const [copied, setCopied] = useState(false)

  const { enqueueSnackbar } = useSnackbar()
  const onDownloadSLOCert = () => 
    dispatch(crudGetOneEx('settings-saml-slo-cert', 0, '', {
      onFailure: (err: any) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Download failed: ${errMsg}`, { variant: 'error' })
      }
    }))

  return (
    <OdForm formikCtrl={formikCtrl}>

      <Card>
        <CardContent>
          <Grid
            container
            spacing={4}
          >
            <Grid item xs={12} >
              <FormGroup sx={{ display: 'block', '& span.MuiTouchRipple-root': { color: 'primary.main' } }}>
                <FormControlLabel 
                  control={
                    <Switch name="isEnabled" checked={values.isEnabled} disabled={!hasWritePermit} 
                      onChange={(e:any) => { 
                        setFieldTouched("isEnabled", true); 
                        handleChange(e)
                      }} 
                    />
                  } 
                  label="Enable Single Sign-ON (SSO)" 
                />
              </FormGroup>
            </Grid>
            {(!preData?.organizationId && !values.isEnabled) ? null :
              <>
              <Grid item xs={12} >
                <FormControl component="fieldset">
                  <FormLabel component="legend">Protocol</FormLabel>
                  <RadioGroup row name="protocol" value={values.protocol} onChange={handleChange}>
                    <FormControlLabel value={SsoProtocol.SAML+''} control={<Radio />} label="SAML2.0" />
                    <FormControlLabel value={SsoProtocol.OIDC+''} control={<Radio />} label="OpenID Connect" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {+values.protocol !== SsoProtocol.SAML ? null :
                <>
                  <Grid item xs={12} >
                    <TextField label="Service Provider Entity Id*" name="samlEntityId" disabled={!hasWritePermit} />
                  </Grid>
                  <Grid item xs={12} >
                    <TextField label="Sign In URL*" name="samlLoginUrl" disabled={!hasWritePermit} />
                  </Grid>
                  <Grid item xs={12} >
                    <FormControl 
                      fullWidth 
                      component="fieldset" 
                      error={Boolean(touched.samlCertificate && errors.samlCertificate)}
                    >
                      <InputLabel htmlFor="cert-file" shrink={true} >Identity Provider X.509 Certificate*</InputLabel>
                      <OutlinedInput 
                        id="cert-file" 
                        type="file" 
                        label="Identity Provider X.509 Certificate*" 
                        disabled={!hasWritePermit}
                        notched={true}
                        name="samlCertificate"
                        value={values.samlCertificate}
                        onChange={handleCertFileChange}
                      />
                      <FormHelperText>{touched.samlCertificate && errors.samlCertificate}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} >
                    <Tooltip title={copied ? 'Copied' : 'Click to Copy'} placement="top">
                      <Box>
                        <ReadonlyField 
                          id="saml-service-url"
                          label="SAML Assertion Consumer Service URL" 
                          name="samlAssertionConsumerServiceUrl" 
                          value={preData?.samlAssertionConsumerServiceUrl || ''}
                          {...autoCopyTextFieldProps(() => setCopied(true), () => setTimeout(() => setCopied(false), 500))}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} >
                    <TextField label="On Logout, Sign Out IdP at URL" name="samlLogoutUrl" disabled={!hasWritePermit} />
                    <FormHelperText>You can download the related OnData signing certificate from{" "}
                      <Link onClick={onDownloadSLOCert}>here</Link>.
                    </FormHelperText>
                  </Grid>
                  {/* <Grid item xs={12} >
                    <TextField label="samlSpIssuer*" name="samlSpIssuer" />
                  </Grid> */}
                </>
              }
              {+values.protocol !== SsoProtocol.OIDC ? null :
                <>
                  <Grid item xs={12} >
                    <TextField label="Well Known Configuration URL*" name="oidcDomain" disabled={!hasWritePermit} />
                  </Grid>
                  <Grid item xs={12} >
                    <TextField label="Client Id*" name="oidcClientId" disabled={!hasWritePermit} />
                  </Grid>
                  <Grid item xs={12} >
                    <FormControl 
                      fullWidth 
                      variant="outlined"
                      error={Boolean(touched.oidcClientSecret && errors.oidcClientSecret)}
                    >
                      <InputLabel htmlFor="outlined-adornment-password">Client Secret*</InputLabel>
                      <OutlinedInput
                        id="outlined-adornment-password"
                        type={showPass ? 'text' : 'password'}
                        name="oidcClientSecret"
                        disabled={!hasWritePermit}
                        value={values.oidcClientSecret}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        endAdornment={endAdornment}
                        label="Client Secret*"
                        sx={{ '& input[type=password]::-ms-reveal': { display: 'none' } }}
                      />
                      <FormHelperText>{touched.oidcClientSecret && errors.oidcClientSecret}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} >
                    <Tooltip title={copied ? 'Copied' : 'Click to Copy'} placement="top">
                      <Box>
                        <ReadonlyField 
                          id="login-redirect-url"
                          label="Login Redirect URL" 
                          name="oidcRedirectUrl" 
                          value={preData?.oidcRedirectUrl || ''}
                          {...autoCopyTextFieldProps(() => setCopied(true), () => setTimeout(() => setCopied(false), 500))}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} >
                    <Tooltip title={copied ? 'Copied' : 'Click to Copy'} placement="top">
                      <Box>
                        <ReadonlyField 
                          id="logout-redirect-url"
                          label="Logout Redirect URL" 
                          name="oidcLogoutRedirectUrl" 
                          value={window.location.origin || ''}
                          {...autoCopyTextFieldProps(() => setCopied(true), () => setTimeout(() => setCopied(false), 500))}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} >
                    <TextField label="On Logout, Sign Out IdP at URL" name="oidcLogoutUrl" disabled={!hasWritePermit} />
                  </Grid>
                </>
              }
              </>
            }
          </Grid>
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {errors.submit}
              </FormHelperText>
            </Box>
          )}
        </CardContent>
        {(!preData?.organizationId && !values.isEnabled) || !hasWritePermit ? null :
          <Box sx={{ p: 2 }} >
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
            >
              Update SSO Configuration
            </Button>
          </Box>
        }
      </Card>

    </OdForm>
  )
}

export default SsoConfig;
